<template>
  <div class="grid-map-menu-body">
    <tab-form
      class="grid-map-tab-form"
      :tabList="favoriteList"
      :changeSelectedTabIndex="changeSelectedTabIndex"
      :selectedTabIndex="selectedTabIndex"
    />
    <search-form
      class="grid-map-search-form"
      :placeholder="placeholder"
      :keyword="keyword"
      :handleInput="searchVessel"
    />

    <component
      :is="scrollbarTag"
      class="grid-map-scroll-area--customizer"
      :settings="settings"
      :key="$vs.rtl"
    >
      <div class="scrollable-area">
        <div
          class="group-container"
          v-for="group in displayFavoriteList[selectedTabIndex].groupList"
          :key="group.documentId"
        >
          <group-bar
            class="aisea-pb-6"
            :group="group"
            :switchGroupContent="switchGroupContent"
            :isTargetGroupContentOpened="isTargetGroupContentOpened"
          ></group-bar>

          <transition name="smooth">
            <ul
              class="mt-1"
              v-if="isTargetGroupContentOpened(group.documentId)"
            >
              <li
                class="aisea-pb-6 aisea-pl-4 aisea-pr-4"
                v-for="vesselId in group.vessels"
                :key="vesselId"
              >
                <vessel-card
                  class="vessel-card"
                  :vessel="getSelectedVesselById(vesselId)"
                  :jumpToVessel="jumpToVessel"
                  :isSelected="trackingTarget === vesselId"
                  :vesselPosition="resultVesselPosition(vesselId)"
                ></vessel-card>
              </li>
            </ul>
          </transition>
        </div>
      </div>
    </component>
  </div>
</template>

<script>
/**
 * 【右メニュー】選択船メニューを描画するためのコンポーネント
 * @parent O.RightMenuBase
 */
import { DateUtil } from "@/utils";
import moment from "moment-timezone";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import TabForm from "@/components/molecules/TabForm";
import SearchForm from "@/components/molecules/SearchForm";
import GroupBar from "@/components/molecules/GroupBar";
import VesselCard from "@/components/molecules/VesselCard";
import { createNamespacedHelpers } from "vuex";
import _ from "lodash";
import FireStoreService from "@/services/fireStoreService";

const { mapState: mapStateOfLiveMap } = createNamespacedHelpers("liveMap");

const {
  mapActions: mapActionsOfGridMap,
  mapState: mapStateOfGridMap,
  mapMutations: mapMutationsOfGridMap,
  mapGetters: mapGettersOfGridMap,
} = createNamespacedHelpers("gridMap");

export default {
  name: "O.SelectedVessel",
  components: {
    VuePerfectScrollbar,
    TabForm,
    SearchForm,
    GroupBar,
    VesselCard,
  },
  props: {
    /** スクロールの設定 */
    settings: {
      type: Object,
      required: true,
    },
    vesselPositions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      // 検索機能で使用
      placeholder: "キーワードを入力",
      keyword: "",
      // 表示中の船グループを格納
      openedContentGroups: [],
    };
  },
  computed: {
    ...mapStateOfLiveMap({
      // お気に入りに登録している船情報のリスト
      selectedVessels: (state) => state.monitoringVessels,
      // お気に入りラベルとグループリストを紐づけたリスト
      favoriteList: (state) => state.favoriteList,
    }),
    ...mapStateOfGridMap({
      // アクティブなグリッドのmapIdを格納
      activeSidebarForGrid: (state) => state.activeSidebarForGrid,
      // 4グリッド分の設定情報を格納
      allGridMapSettings: (state) => state.allGridMapSettings,
    }),
    ...mapGettersOfGridMap(["getTrackingTarget"]),
    // ソート済み、フィルター済みのリストを返す
    displayFavoriteList() {
      return this.favoriteList.map((favorite) => {
        return {
          label: favorite.label,
          groupList: favorite.groupList
            .map((group) => {
              return {
                documentId: group.documentId,
                label: group.label,
                color: group.color,
                sort: group.sort,
                vessels: this.sortVessels(
                  group.vessels.filter((vesselId) =>
                    this.isVesselIncludeKeyword(vesselId),
                  ),
                ),
              };
            })
            .sort((a, b) => {
              return a.sort - b.sort;
            }),
        };
      });
    },
    // 選択中のタブインデックス
    selectedTabIndex() {
      const value = this.activeSidebarForGrid;
      if (!value.mapId || !this.allGridMapSettings) {
        return 0;
      }
      const mapId = value.mapId;
      const targetSetting = this.allGridMapSettings.find((setting) => {
        return setting.map_id === mapId;
      });
      if (!targetSetting || !targetSetting.selected_favorite_index) {
        return 0;
      }
      return targetSetting.selected_favorite_index;
    },
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    // FireStoreで追跡設定しているターゲットがvesselならIDを返す
    trackingTarget() {
      return this.getTrackingTarget({
        mapId: this.activeSidebarForGrid.mapId,
        type: "vessel",
      });
    },
  },
  methods: {
    ...mapActionsOfGridMap(["getLastPositionWithTimeout"]),
    ...mapMutationsOfGridMap([
      "UPDATE_GRID_MAP_SETTINGS",
      "UPDATE_LAST_TRACKING_POSITION",
      "SET_EXPIRED_POPUP_FOR_GRID",
    ]),
    // 検索用メソッド
    searchVessel(nextKeyword) {
      this.keyword = nextKeyword;
    },
    // IDから該当の船を取得
    getSelectedVesselById(vesselId) {
      const index = this.selectedVessels.findIndex(
        (vessel) => vessel.id == vesselId,
      );
      if (index !== -1) {
        return this.selectedVessels[index];
      }
      return null;
    },
    // 選択したタブインデックス
    async changeSelectedTabIndex(index) {
      if (!this.activeSidebarForGrid.mapId) {
        return;
      }
      const mapId = this.activeSidebarForGrid.mapId;
      // store(vuex)更新
      this.UPDATE_GRID_MAP_SETTINGS({
        map_id: mapId,
        selected_favorite_index: index,
      });
      // firestore更新
      await FireStoreService.setGridMapSettings(mapId, {
        selected_favorite_index: index,
      });
    },
    // 船の並び替えロジック
    sortVessels(vesselList) {
      const vesselListWithName = vesselList.map((vesselId) => {
        const targetVessel = this.selectedVessels.find((vessel) => {
          return vessel.id == vesselId;
        });
        if (!targetVessel) {
          return null;
        }
        return {
          id: vesselId,
          name: targetVessel.name_ja
            ? targetVessel.name_ja
            : targetVessel.name
            ? targetVessel.name
            : "",
        };
      });
      const sortedVesselList = _.sortBy(vesselListWithName, "name").map(
        (vessel) => {
          if (!vessel || !vessel.id) {
            return [];
          }
          return vessel.id;
        },
      );
      return sortedVesselList;
    },
    // キーワード検索
    isVesselIncludeKeyword(vesselId) {
      if (!this.keyword.length) {
        return true;
      }
      const targetVessel = this.selectedVessels.find(
        (vessel) => vessel.id == vesselId,
      );
      if (!targetVessel) {
        return false;
      }
      const searchTargetArray = [targetVessel.name_ja, targetVessel.name];
      return searchTargetArray.some((item) => {
        if (!item) {
          return false;
        }
        return (
          item.toUpperCase().indexOf(this.keyword.trim().toUpperCase()) !== -1
        );
      });
    },
    // 船情報を表示・非表示の切替
    switchGroupContent(groupDocumentId) {
      const index = this.openedContentGroups.findIndex(
        (element) => element == groupDocumentId,
      );
      if (index == -1) {
        this.openedContentGroups.push(groupDocumentId);
      } else {
        this.openedContentGroups.splice(index, 1);
      }
    },
    // 船情報の表示・非表示フラグを取得
    isTargetGroupContentOpened(groupDocumentId) {
      return this.openedContentGroups.includes(groupDocumentId);
    },
    // IDが一致する船のポジションを返す
    resultVesselPosition(vesselId) {
      const vessel = this.vesselPositions.find((vesselPosition) => {
        return vesselPosition.vesselId === vesselId;
      });
      if (vessel) {
        return vessel.vesselPosition;
      }
      return null;
    },
    async jumpToVessel(vessel) {
      if (!vessel) {
        this.showFailureMessage();
        return;
      }
      const mapId = this.activeSidebarForGrid.mapId;
      if (!mapId) return;
      // 選択中のカードをクリックしたら選択を解除しデフォルトポジションに戻る
      if (this.trackingTarget === vessel.id) {
        this.UPDATE_LAST_TRACKING_POSITION({
          mapId: mapId,
          center: [],
        });
        return;
      }
      this.$vs.loading({ container: `#${mapId}.vs-con-loading__container` });
      const lastPosition = await this.getLastPositionWithTimeout(vessel.id);
      this.$vs.loading.close(
        `#${mapId}.vs-con-loading__container > .con-vs-loading`,
      );
      if (
        !lastPosition ||
        lastPosition.longitude == null ||
        lastPosition.latitude == null
      ) {
        this.showFailureMessage();
        return;
      }
      let center = [lastPosition.longitude, lastPosition.latitude];
      const trackingTarget = { type: "vessel", id: vessel.id };
      // store(vuex)更新
      this.UPDATE_LAST_TRACKING_POSITION({
        mapId: mapId,
        center: center,
      });
      this.UPDATE_GRID_MAP_SETTINGS({
        map_id: mapId,
        tracking_target: trackingTarget,
      });
      // firestore更新
      await FireStoreService.setGridMapSettings(mapId, {
        tracking_target: trackingTarget,
      });
      this.showNotifyTimeout(lastPosition);
    },
    // 最終計測時間が10分前より遅い場合はポップアップメッセージを表示する
    showNotifyTimeout(lastPosition) {
      const tenMinutesAgo = moment().subtract(10, "minutes").utc().unix();
      const lastPosAt = moment(lastPosition.last_position_at).unix();

      if (lastPosAt < tenMinutesAgo) {
        const name = lastPosition.vessel_name_ja || lastPosition.vessel_name;
        const dateFormat = moment(lastPosition.last_position_at).format(
          "YYYY/MM/DD HH:mm",
        );
        let elapsed = DateUtil.elapsedTime(dateFormat);
        const popupObject = {
          closeOnClick: false,
          closeButton: true,
          offset: 15,
          className: "expired-vessel-live-position-popup",
        };
        const center = [lastPosition.longitude, lastPosition.latitude];
        const text = `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`;
        this.createExpiredPopup(popupObject, center, text);
      }
    },
    // 座標取得に失敗したときのメッセージ
    showFailureMessage() {
      this.$vs.notify({
        text: "位置情報が取得できません。",
        color: "danger",
        iconPack: "feather",
        icon: "icon-alert-circle",
        position: "top-center",
      });
    },
    // グリッドマップにポップアップメッセージを表示
    createExpiredPopup(popupObject, center, text) {
      this.SET_EXPIRED_POPUP_FOR_GRID({
        mapId: this.activeSidebarForGrid.mapId,
        popupObject: popupObject,
        center: center,
        text: text,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.smooth-enter-active,
.smooth-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.smooth-enter,
.smooth-leave-to {
  opacity: 0;
}
</style>
